import React, { useState } from "react";
import { useModal } from "@context/ModalContext";
import { toast } from "react-toastify";
import styled from "@emotion/styled";
import { AlertTriangle, X, Trash2, AlertCircle } from "lucide-react";

interface DeleteModalProps {
  state: string;
  api: any;
  id: number | string;
  itemName?: string;
  itemType?: string;
  additionalInfo?: string;
  onSuccess?: () => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({
  state,
  api,
  id,
  itemName = "this item",
  itemType = "item",
  additionalInfo,
  onSuccess,
}) => {
  const { hideModal } = useModal();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await api.remove({ state, id });
      if (onSuccess) onSuccess();
      hideModal();
    } catch (error) {
       console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <ModalHeader>
        <WarningIconContainer>
          <AlertTriangle size={24} />
        </WarningIconContainer>
        <ModalTitle>Confirm Deletion</ModalTitle>
      </ModalHeader>

      <ModalContent>
        <WarningText>
          You are about to delete <strong>"{itemName}"</strong>
        </WarningText>

        {additionalInfo && (
          <AdditionalInfo>
            <AlertCircle size={16} />
            {additionalInfo}
          </AdditionalInfo>
        )}

        <PermanentWarning>
          This action cannot be undone. This will permanently delete the{" "}
          {itemType} and remove all associated data.
        </PermanentWarning>
      </ModalContent>

      <ModalFooter>
        <CancelButton onClick={hideModal} disabled={isDeleting}>Cancel</CancelButton>
        <DeleteButton onClick={handleConfirm} disabled={isDeleting}>
          <Trash2 size={18} />
          {isDeleting ? "Deleting..." : "Delete"}
        </DeleteButton>
      </ModalFooter>
    </>
  );
};

export default DeleteModal;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 480px;
  width: 100%;
  overflow: hidden;
  border: 1px solid #e2e8f0;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  position: relative;
`;

const WarningIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: #fef2f2;
  border-radius: 50%;
  color: #dc2626;
  border: 2px solid #fecaca;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #f8fafc;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #475569;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const WarningText = styled.p`
  color: #475569;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0 0 1rem 0;
  text-align: center;

  strong {
    color: #1e293b;
    font-weight: 600;
  }
`;

const AdditionalInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: #fffbeb;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 1rem 0;
  color: #92400e;
  font-size: 0.875rem;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
  }
`;

const PermanentWarning = styled.p`
  color: #ef4444;
  font-size: 0.875rem;
  line-height: 1.4;
  text-align: center;
  margin: 1rem 0 0 0;
  padding: 0.75rem;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #f1f5f9;
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const DeleteButton = styled.button`
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: none;
  background: #dc2626;
  color: #fff;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.2s ease;

  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;
